import { MobXProviderContext } from "mobx-react";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { FC, useContext, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { getErrorTitle } from "../../../../constants/constants";
import { ComplianceSection } from "../../../../models/manageWebSite";
import { normalizePlainText } from "../../../../utils/securityUtils";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../../components/general/uiSurfaceStyles";

interface EditModalProps {
  data: ComplianceSection | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  homeId: string | null;
}

const splitLines = (value: string) =>
  value
    .split("\n")
    .map((item) => normalizePlainText(item, { maxLength: 180 }))
    .filter(Boolean);

const EditModal: FC<EditModalProps> = ({ isOpen, toggleModal, flag, setFlag, data }) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);

  const { control, formState: { errors }, handleSubmit, reset } = useForm({
    defaultValues: {
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      licenseNumber: data?.licenseNumber || "",
      licenseStatus: data?.licenseStatus || "",
      certifications: (data?.certifications || []).join("\n"),
      trainingHighlights: (data?.trainingHighlights || []).join("\n"),
      complianceNotes: data?.complianceNotes || "",
      nurseDelegationSupport: data?.nurseDelegationSupport || "",
      medicationManagementSupport: data?.medicationManagementSupport || "",
      backgroundCheckSummary: data?.backgroundCheckSummary || "",
    },
  });

  useEffect(() => {
    reset({
      enabled: data?.enabled || false,
      title: data?.title || "",
      description: data?.description || "",
      licenseNumber: data?.licenseNumber || "",
      licenseStatus: data?.licenseStatus || "",
      certifications: (data?.certifications || []).join("\n"),
      trainingHighlights: (data?.trainingHighlights || []).join("\n"),
      complianceNotes: data?.complianceNotes || "",
      nurseDelegationSupport: data?.nurseDelegationSupport || "",
      medicationManagementSupport: data?.medicationManagementSupport || "",
      backgroundCheckSummary: data?.backgroundCheckSummary || "",
    });
  }, [data, isOpen, reset]);

  const onSubmit = async (formData: any) => {
    try {
      await backendAPIStore.init();
      await backendAPIStore.updateTemplateSection({
        ...data,
        enabled: !!formData.enabled,
        title: normalizePlainText(formData.title, { maxLength: 140 }),
        description: normalizePlainText(formData.description, { multiline: true, maxLength: 1200 }),
        licenseNumber: normalizePlainText(formData.licenseNumber, { maxLength: 80 }),
        licenseStatus: normalizePlainText(formData.licenseStatus, { maxLength: 80 }),
        certifications: splitLines(formData.certifications),
        trainingHighlights: splitLines(formData.trainingHighlights),
        complianceNotes: normalizePlainText(formData.complianceNotes, { multiline: true, maxLength: 1200 }),
        nurseDelegationSupport: normalizePlainText(formData.nurseDelegationSupport, { multiline: true, maxLength: 500 }),
        medicationManagementSupport: normalizePlainText(formData.medicationManagementSupport, { multiline: true, maxLength: 500 }),
        backgroundCheckSummary: normalizePlainText(formData.backgroundCheckSummary, { multiline: true, maxLength: 500 }),
        modifiedAt: new Date().toISOString(),
      });
      toggleModal(false);
      setFlag(!flag);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: "Unable to save the compliance page.", life: 3000 });
    }
  };

  const footerContent = renderDialogFooter({
    errorMessage: null,
    loading: false,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Compliance Page",
  });

  return (
    <Dialog
      visible={isOpen}
      modal
      header={renderSurfaceHeader("Compliance Page", "Manage licensing, certifications, training, and trust-building compliance information.", "pi pi-verified")}
      footer={footerContent}
      style={appDialogStyle}
      headerStyle={appDialogHeaderStyle}
      contentStyle={appDialogContentStyle}
      onHide={() => toggleModal(false)}
    >
      <Toast ref={toast} />
      <div style={appAccentPanelStyle} className="mb-3">
        <div style={{ fontWeight: 700, color: "#9a3412" }}>Washington adult family home trust page</div>
        <div className="mt-2" style={appMutedTextStyle}>Use this page to present provider-entered licensing and compliance information in a clean, family-friendly format.</div>
      </div>
      <form>
        <div style={appFormSectionStyle}>
          <div className="flex align-items-center gap-2">
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Checkbox inputId="complianceEnabled" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
              )}
            />
            <label htmlFor="complianceEnabled" style={{ fontWeight: 600 }}>Add Compliance page into the website</label>
          </div>
        </div>
        {[
          { name: "title", label: "Page Title", rows: 3 },
          { name: "description", label: "Description", rows: 5 },
          { name: "licenseNumber", label: "License Number", rows: 2 },
          { name: "licenseStatus", label: "License Status", rows: 2 },
          { name: "certifications", label: "Certifications", rows: 5 },
          { name: "trainingHighlights", label: "Training Highlights", rows: 5 },
          { name: "complianceNotes", label: "Compliance Notes", rows: 5 },
          { name: "nurseDelegationSupport", label: "Nurse Delegation Support", rows: 3 },
          { name: "medicationManagementSupport", label: "Medication Management Support", rows: 3 },
          { name: "backgroundCheckSummary", label: "Background Check Summary", rows: 3 },
        ].map(({ name, label, rows }) => (
          <div style={{ ...appFormSectionStyle, marginTop: "1rem" }} key={name}>
            <label htmlFor={name}><strong>{label}</strong></label>
            <Controller
              name={name as any}
              control={control}
              render={({ field }) => <InputTextarea id={name} {...field} rows={rows} style={{ width: "100%" }} className={errors[name as keyof typeof errors] ? "p-invalid" : ""} />}
            />
          </div>
        ))}
      </form>
    </Dialog>
  );
};

export default EditModal;
